/**
 * SignaturePad — the finger-signature sheet, shared by the service invoice
 * (customer sign-off on the work + total) and the installer app (an add-on
 * sold on site, the walkthrough sign-off). Output is a small PNG data URL.
 * Moved out of TechApp.tsx (FIELD-CREWS-01) so jobVisit / installWork can use
 * it without importing TechApp (which imports them — a cycle).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check } from "lucide-react";
import { cn } from "@/lib/cn";

const money$ = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SignaturePad({ customerName, total, title, subtitle, consent, doneLabel, onDone, onClose }: {
  customerName: string;
  /** The amount being approved (the subtitle + consent line show it). */
  total?: number | null;
  /** Header, default "Customer sign-off". */
  title?: string;
  /** Under the header, default "<customer> · <total>". */
  subtitle?: string;
  /** The line under the box that says what signing means. */
  consent?: string;
  doneLabel?: string;
  onDone: (dataUrl: string) => void;
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const last = useRef<[number, number] | null>(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = 1000;
    c.height = 400;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const toPt = (e: React.PointerEvent): [number, number] => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return [((e.clientX - r.left) * c.width) / r.width, ((e.clientY - r.top) * c.height) / r.height];
  };
  const stroke = (a: [number, number], b: [number, number]) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.strokeStyle = "#17293e";
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
    ctx.stroke();
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
  };
  const first = customerName.split(" ")[0] || "The customer";
  const hasTotal = total != null && Number.isFinite(total);

  return (
    <div className="fixed inset-0 z-[60] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Cancel signature">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-slate-900">{title ?? "Customer sign-off"}</div>
          <div className="truncate text-[11px] text-slate-500">{subtitle ?? `${customerName}${hasTotal ? ` · ${money$(total!)}` : ""}`}</div>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-3 p-4">
        <p className="text-center text-xs text-slate-500">Sign inside the box.</p>
        <div className="rounded-3xl border-2 border-dashed border-slate-300 bg-white p-1.5">
          <canvas
            ref={canvasRef}
            onPointerDown={(e) => {
              // Capture keeps the stroke when a finger slides off the box; it
              // can throw for a pointer the browser already released — harmless.
              try { (e.target as Element).setPointerCapture(e.pointerId); } catch { /* fine */ }
              last.current = toPt(e);
            }}
            onPointerMove={(e) => {
              if (!last.current) return;
              const p = toPt(e);
              stroke(last.current, p);
              last.current = p;
              if (!hasInk) setHasInk(true);
            }}
            onPointerUp={() => { last.current = null; }}
            onPointerCancel={() => { last.current = null; }}
            className="h-44 w-full rounded-2xl"
            style={{ touchAction: "none" }}
          />
        </div>
        <p className="text-center text-[10.5px] leading-snug text-slate-400">
          {consent ?? `By signing, ${first} approves the work performed${hasTotal ? ` and the total of ${money$(total!)}` : ""}.`}
        </p>
      </div>
      <footer className="flex gap-2 border-t border-slate-200 bg-white p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
        <button type="button" onClick={clear} className="flex-1 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-bold text-slate-600 active:bg-slate-100">
          Clear
        </button>
        <button
          type="button"
          disabled={!hasInk}
          onClick={() => { const c = canvasRef.current; if (c) onDone(c.toDataURL("image/png")); }}
          className={cn("flex-[2] rounded-2xl py-3.5 text-sm font-bold text-white", hasInk ? "bg-emerald-600 active:opacity-90" : "bg-slate-300")}
        >
          <Check className="mr-1 inline h-4 w-4" /> {doneLabel ?? "Done — looks good"}
        </button>
      </footer>
    </div>
  );
}
